import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';
import type { ConversationView, MessageView } from '../api/client';
import { inPatch, patchIn, usePatch, useLivePatches } from '../lib/patch';
import { goingText } from '../lib/patchText';
import { Button } from './ui';

/**
 * Patches in Messages: "Patch in" in a conversation's header, "Patch going: Sam, Joey · Join" under
 * it, and Join on the note a Patch leaves when it starts. A Patch can be started in a direct
 * message, or in a channel someone has joined; never a text conversation (Patches are app-only).
 * Nothing shows when Patches aren't switched on.
 */

const patchable = (c: ConversationView) => c.kind === 'dm' || (c.kind === 'channel' && c.joined && !c.archived);
const titleOf = (c: ConversationView) => (c.kind === 'channel' ? `#${c.title}` : c.title);
/** Who a Patch started here rings: everyone in a direct message (lib/patch leaves out this person). */
const rungIn = (c: ConversationView) => (c.kind === 'dm' ? c.people.map((p) => p.id) : []);

interface Props {
  domain: string;
  conversation: ConversationView;
}

/** "Patch in": starts the conversation's Patch, or joins the one going. */
export function PatchInButton({ domain, conversation: c }: Props) {
  const live = useLivePatches(domain);
  const s = usePatch();
  if (!live.enabled || !patchable(c)) return null;
  const here = inPatch(s, domain, c.id);
  return (
    <Button
      size="small"
      variant={here ? 'secondary' : 'primary'}
      icon="headset"
      className="patch-in"
      onClick={() => void patchIn({ tenant: domain, conversationId: c.id, title: titleOf(c), rung: rungIn(c) })}
      title={here ? 'Open the Patch' : 'Start a quick call in the app, or join the one going'}
    >
      {here ? 'Patched in' : 'Patch in'}
    </Button>
  );
}

/**
 * "Patch going: Sam, Joey · Join", for people in the conversation who aren't in it.
 *
 * Opened from the "patched you in" notification (`?join=patch`, 1.12.11): straight into the Patch
 * while it's still going, as the notification says. Once it's over, just the conversation.
 */
export function PatchGoingBar({ domain, conversation: c }: Props) {
  const live = useLivePatches(domain);
  const s = usePatch();
  const p = live.patchIn(c.id);
  const [params, setParams] = useSearchParams();
  const joining = params.get('join') === 'patch';
  const here = inPatch(s, domain, c.id);
  const going = Boolean(live.enabled && p && p.people.length && patchable(c));
  const done = useRef('');
  useEffect(() => {
    if (!joining || !live.loaded || done.current === c.id) return;
    done.current = c.id;
    if (going && !here) void patchIn({ tenant: domain, conversationId: c.id, title: titleOf(c), rung: rungIn(c) });
    setParams(
      (q) => {
        q.delete('join');
        return q;
      },
      { replace: true }
    );
  }, [joining, live.loaded, going, here, domain, c, setParams]);
  if (!live.enabled || !p || !p.people.length || !patchable(c) || inPatch(s, domain, c.id)) return null;
  return (
    <div className="patch-going" role="status" aria-label="Patch going">
      <span className="patch-dot speaking" aria-hidden="true" />
      <span className="patch-going-text">{goingText(p.people.map((x) => x.name))}</span>
      <span className="patch-going-sep" aria-hidden="true">
        ·
      </span>
      <Button size="small" variant="primary" icon="headset" onClick={() => void patchIn({ tenant: domain, conversationId: c.id, title: titleOf(c), rung: rungIn(c) })}>
        Join
      </Button>
    </div>
  );
}

/** Join, on "Joey started a Patch", while that Patch is still going. */
export function PatchNoteJoin({ domain, conversation: c, message }: Props & { message: MessageView }) {
  const live = useLivePatches(domain);
  const s = usePatch();
  const p = live.patchIn(c.id);
  if (!p || p.id !== message.meta?.patchId || !patchable(c) || inPatch(s, domain, c.id)) return null;
  return (
    <Button size="small" variant="ghost" aria-label={`Join the Patch ${message.body.replace(/ started a Patch$/, '')} started`} onClick={() => void patchIn({ tenant: domain, conversationId: c.id, title: titleOf(c), rung: rungIn(c) })}>
      Join
    </Button>
  );
}
